import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AuthUser } from "./app-home/_objects_home";
import { fetchJSON, requestGet, requestPost } from "./utils/network";
import PanelWaiting from "./widget/panel-waiting";

let legacyBridgeAuthenticatedData: AuthUser | null = null;
export const getSessionThroughLegacyBridge = () =>
  legacyBridgeAuthenticatedData;

export type AuthContextType = {
  authenticatedData: AuthUser | null;
  isAuthenticated: () => boolean;
  setAuthenticatedData: (data: AuthUser | null) => void;
  getList: (listName: string) => string[];
  logout: () => void;
  isAuthorized: (transaction?: string) => boolean;
};

// For using it in other components
export const AuthContext = createContext<AuthContextType | null>(null);

// For the main app, only once
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authenticatedData, setAuthenticatedDataInState] =
    useState<AuthUser | null>(null);

  function setAuthenticatedData(data: AuthUser | null) {
    setAuthenticatedDataInState(data);
  }

  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  legacyBridgeAuthenticatedData = authenticatedData;

  useEffect(() => {
    fetchJSON<AuthUser>(requestGet("/api/auth/settings"))
      .then((authenticatedData) => setAuthenticatedData(authenticatedData))
      .catch(() => {
        setAuthenticatedData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const isAuthenticated = () => authenticatedData != null;

  const logout = () => {
    setLoading(true);
    fetchJSON(requestPost("/api/auth/logout"))
      .then(() => setAuthenticatedData(null))
      .then(() => navigate("/login"))
      .finally(() => setLoading(false));
  };

  const isAuthorized = (transaction?: string): boolean => {
    if (!transaction) {
      return true;
    }

    if (!authenticatedData) {
      return false;
    }

    return authenticatedData.authorized.includes(transaction);
  };

  const getList = (listName: string): string[] => {
    if (authenticatedData == null) {
      throw new Error(
        `Config(getList): Trying to get list too early: ${listName}`
      );
    }

    if (listName == "") {
      return ["list-is-empty"];
    }

    const allLists: Record<string, string[]> = authenticatedData.lists;
    if (listName in allLists) {
      return allLists[listName];
    }

    throw new Error(
      `Config(getList): List not available: ${listName}: L=[${Object.keys(allLists).join(",")}]`
    );
  };

  if (loading) {
    return <PanelWaiting message="Loading application..." />;
  }

  return (
    <AuthContext.Provider
      value={{
        authenticatedData,
        setAuthenticatedData,
        isAuthenticated,
        isAuthorized,
        getList,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
